/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {Box, Image, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getPDPath} from '../../../utils/custom-utils'
import useSegment from '../../../integrations/segment/app/hooks/use-segment'

/**
 * Image with text component
 *
 * @param {object} props
 * @param {string} - props.ITCLink - Image Link.
 * @param {string} - props.ITCText - Text Below Image.
 * @param {custom} - props.image - Image.
 * @param {string} - props.heading - Text Overlay.
 * @param {string} - props.alt - The image alt text shown by the component.
 * @returns {React.ReactElement} - ImageWithText component.
 */
export const ImageWithText = ({
    ITCLink,
    ITCText,
    heading,
    alt,
    text_headline,
    text_description,
    custom
}) => {
    const {site, locale} = useMultiSite()
    const hasCaption = ITCText || heading || text_headline
    const categoryUrl = getPDPath(ITCLink, site, locale)
    const imageUrl = custom?.image?.disBaseLink
    const mobileImageUrl = custom?.mobileImage?.disBaseLink || imageUrl
    const tabletImageUrl = custom?.tabletImage?.url || mobileImageUrl
    const segment = useSegment()

    return (
        <Box className={'image-with-text'}>
            <Box
                as="figure"
                className={'image-with-text-figure'}
                position={'relative'}
                margin={0}
                w="100%"
                onClick={(event) => {
                    // segment
                    segment.sendPanelSelectedEvent(event, {})
                }}
            >
                {ITCLink ? (
                    <Link href={categoryUrl}>
                        <picture>
                            <source srcSet={tabletImageUrl} media="(min-width: 48em)" />
                            <source srcSet={imageUrl} media="(min-width: 64em)" />
                            <img
                                className={'image-with-text-image'}
                                data-testid={'image-with-text-image'}
                                src={mobileImageUrl}
                                alt={alt}
                                filter={heading ? 'brightness(40%)' : undefined}
                            />
                        </picture>
                    </Link>
                ) : (
                    <Image
                        className={'image-with-text-image'}
                        data-testid={'image-with-text-image'}
                        src={mobileImageUrl}
                        alt={alt}
                        filter={heading ? 'brightness(40%)' : undefined}
                    />
                )}

                {hasCaption && (
                    <Text as="figcaption" textAlign="center">
                        {text_headline && (
                            <Box className={'image-with-text-heading-container'}>
                                <Text
                                    className={'image-with-text-text-underneath'}
                                    as="h3"
                                    fontSize="2xl"
                                    fontWeight="semibold"
                                    m="1rem 0"
                                >
                                    {/* The `dangerouslySetInnerHTML` is safe to use in this context. */}
                                    {/* The HTML in the response from Page Designer API is already sanitized. */}
                                    <Box
                                        dangerouslySetInnerHTML={{
                                            __html: text_headline
                                        }}
                                    />
                                </Text>
                            </Box>
                        )}
                        {text_description && (
                            <Box>
                                <Text
                                    as="span"
                                    className={'image-with-text-text-underneath'}
                                    fontSize="md"
                                    fontWeight="normal"
                                    lineHeight="base"
                                >
                                    {/* The `dangerouslySetInnerHTML` is safe to use in this context. */}
                                    {/* The HTML in the response from Page Designer API is already sanitized. */}
                                    <Box
                                        dangerouslySetInnerHTML={{
                                            __html: text_description
                                        }}
                                    />
                                </Text>
                            </Box>
                        )}
                    </Text>
                )}
            </Box>
        </Box>
    )
}

ImageWithText.propTypes = {
    ITCLink: PropTypes.string,
    ITCText: PropTypes.string,
    text_headline: PropTypes.string,
    text_description: PropTypes.string,
    heading: PropTypes.string,
    alt: PropTypes.string,
    custom: PropTypes.object
}

export default ImageWithText
